import React, { useState } from 'react';
import { Button, LoadingScroll, PopUp, Sheet } from 'geniebook-ui/dist';
import { useTopUpGenieCreditList } from '@hooks/user.hook';
import TopUpSelection from '@components/subscription/detail/TopUpSelection';
import { useMutation } from '@tanstack/react-query';
import {
  IPlanOverviewDetail,
  ISubjectSubscription,
  ITopUpWorksheetCreditReq,
} from '@models/interfaces/user.interface';
import UserService from '@services/GeniebookAPI/UserServices.services';
import { atomWithStorage } from 'jotai/utils';
import { useSetAtom } from 'jotai';
import { errorAtom } from '@components/subscription/store/SubscriptionAtoms';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { canProceedToSelfServePayment } from '@components/subscription/helper/SubscriptionHelper';
import { DetailUser } from '@models/types/users.type';

export interface TopUpGenieCreditProps {
  onClose: () => void;
  subscription: ISubjectSubscription;
  planDetail: IPlanOverviewDetail;
  userDetail: DetailUser;
}

const paymentWaitingIdAtom = atomWithStorage<string>('payment-waiting-id', '');
const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const TopUpGenieCredit = (props: TopUpGenieCreditProps) => {
  const topUpListQuery = useTopUpGenieCreditList();
  const [selectedId, setSelectedId] = useState(0);
  const setPaymentWaitingId = useSetAtom(paymentWaitingIdAtom);
  const setError = useSetAtom(errorAtom);
  const width = useViewWidth();

  const purchaseMutation = useMutation({
    mutationFn: async (formData: ITopUpWorksheetCreditReq) => {
      return UserService.topUpWorksheetCredit(formData);
    },
    onSuccess: data => {
      if (!data.data.error) {
        setPaymentWaitingId(data.data.data.payment_gateway_transaction_id);
        props.onClose();
        window.open(data.data.data.redirect_url, '_blank');
      } else {
        setError(data.data.error_msg);
      }
    },
  });

  async function handlePurchase() {
    if (
      !selectedId ||
      !canProceedToSelfServePayment(
        props.subscription,
        props.planDetail,
        props.userDetail,
      )
    ) {
      return;
    }

    await purchaseMutation.mutate({
      subject_id: props.subscription.subject_id,
      subscription_topup_id: selectedId,
      finish_url: window.location.href,
    });
  }

  const Content = () => {
    return (
      <div className={'text-left'}>
        <p
          className={
            'font-bold lg:text-xl max-lg:text-md !leading-[1.2] lg:mb-2 max-lg:mb-1 text-primary-navyblue'
          }
        >
          {props.subscription.display_name}
        </p>
        <div
          className={
            'text-primary-navyblue flex justify-between font-semibold lg:text-md max-lg:text-sm !leading-[1.2] lg:mb-8 max-md:mb-2 md:max-lg:mb-4'
          }
        >
          <p>Current Active Worksheets: </p>
          <p className={'font-bold'}>{props.subscription.genie_credit}</p>
        </div>
        <div className={'mb-2'}>
          <p
            className={
              'lg:text-md max-lg:text-sm lg:!leading-[22px] max-lg:!leading-[19px] text-neutral-pencil'
            }
          >
            Accelerate improvement with our AI-personalised worksheets.
          </p>
        </div>
        <div className={'lg:mb-8 md:max-lg:mb-4 max-md:mb-2'}>
          {topUpListQuery.isLoading ? (
            <LoadingScroll />
          ) : (
            topUpListQuery.data?.data.data.map(item => (
              <TopUpSelection
                selectedId={selectedId}
                item={item}
                onSelection={id => setSelectedId(id)}
                key={item.subscription_topup_id}
              />
            ))
          )}
        </div>
        <div>
          <Button
            className={
              '!max-w-full mb-2 font-extrabold hover:transform hover:!scale-[1.01]'
            }
            onClick={handlePurchase}
            disabled={!selectedId}
          >
            Purchase
          </Button>
          <Button
            variant={'secondary'}
            className={'!max-w-full font-extrabold'}
            onClick={props.onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  if (width > breakpoint.lg) {
    return (
      <PopUp
        onClose={() => {}}
        buttonGroup={<></>}
        description={<Content />}
        title={<></>}
        className={'!max-w-[512px] !px-6 !py-2'}
      />
    );
  }
  return (
    <Sheet
      isOpen={true}
      toggleSheet={props.onClose}
      darkMode={false}
      className={'min-h-[250px]'}
    >
      <div className={'px-6 py-4'}>
        <Content />
      </div>
    </Sheet>
  );
};

export default TopUpGenieCredit;
